import { Icon, type IconType } from "@sellar-io/components";
import { ReactNode } from "react";

/**
 * Shared layout primitive for invoice and payment status rows.
 *
 * Renders a zinc-100 card with an optional leading icon, arbitrary children
 * on the left, and an optional badge / CTA on the right.
 */
export const StatusRow = ({
  icon,
  badge,
  children,
}: {
  icon?: IconType;
  badge?: ReactNode;
  children: ReactNode;
}) => (
  <div className="flex items-center justify-between gap-3 rounded-lg bg-zinc-100 p-4">
    <div className="flex w-full items-center gap-2">
      {icon && <Icon icon={icon} size={0.65} />}
      {children}
    </div>
    {badge}
  </div>
);

/**
 * Compact invoice row: file-document icon + text + optional "View / Learn more" CTA.
 */
export const InvoiceStatusRow = ({
  text,
  onView,
  ctaLabel = "View",
}: {
  text: ReactNode;
  onView?: () => void;
  ctaLabel?: string;
}) => (
  <StatusRow icon="FILE_DOCUMENT_OUTLINE">
    <span className="text-wrap">{text}</span>
    {onView && (
      <button
        type="button"
        onClick={onView}
        className="ml-auto flex shrink-0 cursor-pointer items-center gap-0.5 text-sm font-medium text-zinc-700 hover:text-zinc-900"
      >
        {ctaLabel}
        <Icon icon="CHEVRON_RIGHT" size={0.65} />
      </button>
    )}
  </StatusRow>
);
